import { useEffect, useMemo, useState } from 'react';
import { Info, RefreshCw, Users } from 'lucide-react';
import type { PlanDataV2 } from '@/types/planData';

// ---------------------------------------------------------------------------
// PersonnelPanel (blueprint v2 C-5) — §1.6 authorship rules (binding):
//   - Contractor authors the plan; the Qualified Engineer appears ONLY as an
//     engaged reviewer {name, firm, tiaReportRef} — NEVER as author.
//   - Personnel auto-populate from crew data (GET /api/construction-plans/crew,
//     JHA-pattern endpoint) — never re-asked from scratch. Role per person is
//     editable.
// Also renders the Stakeholders & Communications fields (§4.3.1–4.3.5, incl.
// multi-employer creating/exposing/correcting/controlling designations) and
// the JHA linkage field.
// ---------------------------------------------------------------------------

export interface CrewMember {
  id: number;
  firstName: string;
  lastName: string;
  phoneNumber?: string | null;
  role?: string | null;
}

export type PersonnelBlock = PlanDataV2['personnel'];
export type StakeholdersBlock = PlanDataV2['stakeholders'];

export interface PersonnelPanelProps {
  crewMembers: CrewMember[];
  personnel: PersonnelBlock;
  onPersonnelChange: (personnel: PersonnelBlock) => void;
  stakeholders: StakeholdersBlock;
  onStakeholdersChange: (stakeholders: StakeholdersBlock) => void;
  jhaReference: string | null;
  onJhaReferenceChange: (ref: string | null) => void;
  jhaOptions?: { id: number; title: string }[];
  onAutoPopulate?: () => Promise<void> | void;
}

function fullName(m: CrewMember): string {
  return `${m.firstName} ${m.lastName}`.trim();
}

/** Suggest a default assignment from crew roles (heuristic, always editable). */
function suggestAssignment(crew: CrewMember[]): {
  qualifiedPerson: string | null;
  competentRigger: string | null;
  onsiteCompetentPersons: string[];
} {
  const isQP = (m: CrewMember) => /qualified|qp|engineer|manager|foreman/i.test(m.role ?? '');
  const isRigger = (m: CrewMember) => /rigger|rigging/i.test(m.role ?? '');
  const qp = crew.find(isQP);
  const rigger = crew.find(isRigger);
  const onsite = crew
    .filter((m) => m !== qp && m !== rigger && /competent|climber|technician|crew/i.test(m.role ?? ''))
    .map(fullName);
  return {
    qualifiedPerson: qp ? fullName(qp) : null,
    competentRigger: rigger ? fullName(rigger) : null,
    onsiteCompetentPersons: onsite,
  };
}

export function PersonnelPanel({
  crewMembers,
  personnel,
  onPersonnelChange,
  stakeholders,
  onStakeholdersChange,
  jhaReference,
  onJhaReferenceChange,
  jhaOptions = [],
  onAutoPopulate,
}: PersonnelPanelProps) {
  const [qeEnabled, setQeEnabled] = useState(!!personnel.qualifiedEngineer);

  const suggestion = useMemo(() => suggestAssignment(crewMembers), [crewMembers]);

  // Auto-populate once when crew data arrives and the panel is still empty.
  useEffect(() => {
    if (
      crewMembers.length > 0 &&
      personnel.autoPopulatedFrom === 'manual' &&
      !personnel.qualifiedPerson &&
      !personnel.competentRigger &&
      personnel.onsiteCompetentPersons.length === 0
    ) {
      onPersonnelChange({
        ...personnel,
        ...suggestion,
        autoPopulatedFrom: 'crew',
        populatedAt: new Date().toISOString(),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crewMembers]);

  const set = (patch: Partial<PersonnelBlock>) =>
    onPersonnelChange({ ...personnel, ...patch, autoPopulatedFrom: personnel.autoPopulatedFrom });

  const toggleOnsite = (name: string) => {
    const has = personnel.onsiteCompetentPersons.includes(name);
    set({
      onsiteCompetentPersons: has
        ? personnel.onsiteCompetentPersons.filter((n) => n !== name)
        : [...personnel.onsiteCompetentPersons, name],
    });
  };

  return (
    <div className="space-y-6" data-testid="personnel-panel">
      {/* Project Personnel — auto-populated from crew data */}
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <h4 className="font-bold text-gray-900 flex items-center gap-2">
            <Users className="h-4 w-4 text-blue-600" /> Project Personnel (§4.3)
          </h4>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">
              {personnel.autoPopulatedFrom === 'crew'
                ? `Auto-populated from crew data ${new Date(personnel.populatedAt).toLocaleDateString()}`
                : 'Manual entry'}
            </span>
            <button
              type="button"
              onClick={() => {
                if (onAutoPopulate) void onAutoPopulate();
                set({
                  ...suggestion,
                  autoPopulatedFrom: 'crew',
                  populatedAt: new Date().toISOString(),
                });
              }}
              className="px-2 py-1 border border-gray-300 rounded text-xs text-gray-700 flex items-center gap-1"
            >
              <RefreshCw className="h-3 w-3" /> Re-populate from crew
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2 text-sm">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Designated Qualified Person (QP)</label>
            <select
              value={personnel.qualifiedPerson ?? ''}
              onChange={(e) => set({ qualifiedPerson: e.target.value || null })}
              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
            >
              <option value="">— select —</option>
              {crewMembers.map((m) => (
                <option key={m.id} value={fullName(m)}>
                  {fullName(m)} {m.role ? `(${m.role})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Competent Rigger</label>
            <select
              value={personnel.competentRigger ?? ''}
              onChange={(e) => set({ competentRigger: e.target.value || null })}
              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
            >
              <option value="">— select —</option>
              {crewMembers.map((m) => (
                <option key={m.id} value={fullName(m)}>
                  {fullName(m)} {m.role ? `(${m.role})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Onsite Competent Person(s) — role per person editable
            </label>
            <div className="flex flex-wrap gap-2">
              {crewMembers.map((m) => (
                <label
                  key={m.id}
                  className={`flex items-center gap-1.5 px-2 py-1 border rounded text-xs ${
                    personnel.onsiteCompetentPersons.includes(fullName(m))
                      ? 'border-blue-400 bg-blue-50 text-blue-900'
                      : 'border-gray-300 text-gray-700'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={personnel.onsiteCompetentPersons.includes(fullName(m))}
                    onChange={() => toggleOnsite(fullName(m))}
                  />
                  {fullName(m)} {m.role ? `(${m.role})` : ''}
                </label>
              ))}
              {crewMembers.length === 0 && (
                <span className="text-xs text-gray-500 italic">
                  No crew data available — assign manually once the crew endpoint responds.
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* QE of record — engaged reviewer ONLY, never author (§1.6 rule 1) */}
      <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
        <label className="flex items-center gap-2 text-sm font-medium text-gray-800">
          <input
            type="checkbox"
            checked={qeEnabled}
            onChange={(e) => {
              setQeEnabled(e.target.checked);
              if (!e.target.checked) set({ qualifiedEngineer: null });
              else if (!personnel.qualifiedEngineer)
                set({ qualifiedEngineer: { name: '', firm: '', tiaReportRef: '' } });
            }}
          />
          Qualified Engineer of record — engaged reviewer (Class IV / TIA-322-A)
        </label>
        <div className="mt-1 flex items-start gap-2 text-xs text-gray-600">
          <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
          <p>
            The contractor authors this plan. The Qualified Engineer appears ONLY as an engaged reviewer for the
            ANSI/TIA-322-A strength/stability evaluation — never as plan author (A10.48A-2023 §4.3).
          </p>
        </div>
        {qeEnabled && personnel.qualifiedEngineer && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2 text-sm">
            <input
              type="text"
              value={personnel.qualifiedEngineer.name}
              onChange={(e) =>
                set({ qualifiedEngineer: { ...personnel.qualifiedEngineer!, name: e.target.value } })
              }
              placeholder="QE name"
              className="px-2 py-1.5 border border-gray-300 rounded text-sm"
            />
            <input
              type="text"
              value={personnel.qualifiedEngineer.firm}
              onChange={(e) =>
                set({ qualifiedEngineer: { ...personnel.qualifiedEngineer!, firm: e.target.value } })
              }
              placeholder="Firm"
              className="px-2 py-1.5 border border-gray-300 rounded text-sm"
            />
            <input
              type="text"
              value={personnel.qualifiedEngineer.tiaReportRef}
              onChange={(e) =>
                set({
                  qualifiedEngineer: { ...personnel.qualifiedEngineer!, tiaReportRef: e.target.value },
                })
              }
              placeholder="TIA-322-A report reference"
              className="px-2 py-1.5 border border-gray-300 rounded text-sm"
            />
          </div>
        )}
      </div>

      {/* Stakeholders & Communications (§4.3.1–4.3.5) */}
      <div>
        <h4 className="font-bold text-gray-900">Stakeholders &amp; Communications (§4.3.1–4.3.5)</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2 text-sm">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Owner / Client</label>
            <input
              type="text"
              value={stakeholders.ownerClient ?? ''}
              onChange={(e) => onStakeholdersChange({ ...stakeholders, ownerClient: e.target.value })}
              placeholder="Supplies site info + FCC/MPE coordination"
              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Structure Owner</label>
            <input
              type="text"
              value={stakeholders.structureOwner ?? ''}
              onChange={(e) => onStakeholdersChange({ ...stakeholders, structureOwner: e.target.value })}
              placeholder="Coordinates EOR + tenant info transfer"
              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">
              FCC Licensed Entities (comma-separated — RF emission info incl. power reduction / LOTO)
            </label>
            <input
              type="text"
              value={(stakeholders.fccLicensedEntities ?? []).join(', ')}
              onChange={(e) =>
                onStakeholdersChange({
                  ...stakeholders,
                  fccLicensedEntities: e.target.value
                    .split(',')
                    .map((s) => s.trim())
                    .filter(Boolean),
                })
              }
              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
            />
          </div>
        </div>

        {/* Multi-employer designations (§4.3.5) */}
        <div className="mt-3">
          <p className="text-xs font-medium text-gray-600 mb-1">
            Multi-employer worksite designations (§4.3.5) — shared worksite plan
          </p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
            {(['creating', 'exposing', 'correcting', 'controlling'] as const).map((key) => (
              <div key={key}>
                <label className="block text-xs font-medium text-gray-600 mb-1 capitalize">{key} employer</label>
                <input
                  type="text"
                  value={stakeholders.multiEmployer?.[key] ?? ''}
                  onChange={(e) =>
                    onStakeholdersChange({
                      ...stakeholders,
                      multiEmployer: {
                        creating: stakeholders.multiEmployer?.creating ?? '',
                        exposing: stakeholders.multiEmployer?.exposing ?? '',
                        correcting: stakeholders.multiEmployer?.correcting ?? '',
                        controlling: stakeholders.multiEmployer?.controlling ?? '',
                        [key]: e.target.value,
                      },
                    })
                  }
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* JHA linkage (§1.6 rule 3) */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Linked Job Hazard Analysis (JHA) — the standard's companion document
        </label>
        {jhaOptions.length > 0 ? (
          <select
            value={jhaReference ?? ''}
            onChange={(e) => onJhaReferenceChange(e.target.value || null)}
            className="w-full md:max-w-md px-2 py-1.5 border border-gray-300 rounded text-sm"
          >
            <option value="">— none —</option>
            {jhaOptions.map((j) => (
              <option key={j.id} value={`jha-${j.id}`}>
                {j.title}
              </option>
            ))}
          </select>
        ) : (
          <input
            type="text"
            value={jhaReference ?? ''}
            onChange={(e) => onJhaReferenceChange(e.target.value || null)}
            placeholder="JHA reference (id or title)"
            className="w-full md:max-w-md px-2 py-1.5 border border-gray-300 rounded text-sm"
          />
        )}
      </div>
    </div>
  );
}
